import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { Card, CardBody, CardHeader } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Money } from '../components/Money.js'
import { StatusPill } from '../components/StatusPill.js'
import { DataTable, type Column } from '../components/DataTable.js'

export const Route = createFileRoute('/_app/billing')({
  component: BillingPage,
})

interface PlanRow {
  id: string
  name: string
  priceInr: number
  pricingModel: 'monthly' | 'annual' | 'pay_as_you_go'
  description: string | null
  studentLimit: number | null
  trialDays: number
}

interface InvoiceRow {
  id: string
  amountInr: number
  status: 'pending' | 'paid' | 'failed' | 'refunded'
  billingPeriodStart: string
  billingPeriodEnd: string
  paidAt: string | null
  createdAt: string
}

interface AcademyRow {
  id: string
  name: string
  status: string
  planId: string | null
}

function BillingPage() {
  const qc = useQueryClient()

  const academy = useQuery({
    queryKey: ['academy'],
    queryFn: async () => {
      const res = await api<AcademyRow>('/api/v1/academy')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: async () => {
      const res = await api<InvoiceRow[]>('/api/v1/billing/invoices')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const plans = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => {
      const res = await api<PlanRow[]>('/api/v1/billing/plans')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const subscribe = useMutation({
    mutationFn: async (planId: string) => {
      const res = await api<{ subscriptionId: string; shortUrl: string; status: string }>(
        '/api/v1/billing/subscribe',
        { method: 'POST', body: JSON.stringify({ planId }) },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['academy'] })
      window.open(data.shortUrl, '_blank')
    },
  })

  const currentPlan = plans.data?.find((p) => p.id === academy.data?.planId)

  const totalPaidPaise =
    invoices.data
      ?.filter((i) => i.status === 'paid')
      .reduce((sum, i) => sum + i.amountInr, 0) ?? 0

  const totalPendingPaise =
    invoices.data
      ?.filter((i) => i.status === 'pending')
      .reduce((sum, i) => sum + i.amountInr, 0) ?? 0

  const invoiceColumns: Column<InvoiceRow>[] = [
    {
      key: 'period',
      header: 'Period',
      render: (i) => (
        <span>
          {i.billingPeriodStart} → {i.billingPeriodEnd}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (i) => <Money paise={i.amountInr} variant="data" />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (i) => <StatusPill tone={i.status}>{i.status}</StatusPill>,
    },
    {
      key: 'paid',
      header: 'Paid on',
      align: 'right',
      render: (i) =>
        i.paidAt ? (
          <span className="text-sm text-fg-muted">
            {new Date(i.paidAt).toLocaleDateString('en-IN')}
          </span>
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title="Billing"
        description="Subscription, invoices, and payment methods."
      />

      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-12 md:col-span-4">
          <CardBody>
            <p className="text-xs text-fg-subtle">Total paid</p>
            <p className="mt-1 text-2xl font-semibold text-fg">
              <Money paise={totalPaidPaise} />
            </p>
            <p className="mt-1 text-xs text-fg-subtle">
              {invoices.data?.filter((i) => i.status === 'paid').length ?? 0} settled
            </p>
          </CardBody>
        </Card>
        <Card className="col-span-12 md:col-span-4">
          <CardBody>
            <p className="text-xs text-fg-subtle">Pending</p>
            <p
              className={`mt-1 text-2xl font-semibold ${
                totalPendingPaise > 0 ? 'text-warning' : 'text-fg'
              }`}
            >
              <Money paise={totalPendingPaise} />
            </p>
            <p className="mt-1 text-xs text-fg-subtle">
              {invoices.data?.filter((i) => i.status === 'pending').length ?? 0} awaiting
            </p>
          </CardBody>
        </Card>
        <Card className="col-span-12 md:col-span-4">
          <CardBody>
            <p className="text-xs text-fg-subtle">Account status</p>
            <div className="mt-1.5">
              {academy.data?.status ? (
                <StatusPill tone={academy.data.status}>{academy.data.status}</StatusPill>
              ) : (
                <span className="text-sm text-fg-subtle">—</span>
              )}
            </div>
            <p className="mt-2 text-xs text-fg-subtle">
              {academy.data?.name ?? '—'}
            </p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Current plan"
          description={
            currentPlan ? currentPlan.description ?? undefined : 'No plan selected'
          }
        />
        <CardBody>
          {currentPlan ? (
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-base font-semibold text-fg">{currentPlan.name}</p>
                <p className="mt-1 text-sm text-fg-muted">
                  <Money paise={currentPlan.priceInr} className="text-lg font-semibold text-fg" />
                  <span className="ml-1 text-fg-subtle">
                    /{' '}
                    {currentPlan.pricingModel === 'monthly'
                      ? 'month'
                      : currentPlan.pricingModel === 'annual'
                        ? 'year'
                        : 'usage'}
                  </span>
                </p>
              </div>
              <p className="text-xs text-fg-subtle">
                {currentPlan.studentLimit
                  ? `Up to ${currentPlan.studentLimit} students`
                  : 'Unlimited students'}{' '}
                · {currentPlan.trialDays}-day trial
              </p>
            </div>
          ) : (
            <p className="text-sm text-fg-muted">
              Pick a plan below to start your subscription.
            </p>
          )}
        </CardBody>
      </Card>

      <Section title="Available plans" description="Switch any time. Razorpay handles checkout.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {plans.data?.map((plan) => (
            <PlanOption
              key={plan.id}
              plan={plan}
              isCurrent={plan.id === academy.data?.planId}
              isLoading={subscribe.isPending}
              onSubscribe={() => subscribe.mutate(plan.id)}
            />
          ))}
        </div>
      </Section>

      <Section title="Invoice history">
        <DataTable
          columns={invoiceColumns}
          rows={invoices.data ?? []}
          rowKey={(i) => i.id}
          loading={invoices.isLoading}
          empty="No invoices yet."
        />
      </Section>
    </div>
  )
}

function PlanOption({
  plan,
  isCurrent,
  isLoading,
  onSubscribe,
}: {
  plan: PlanRow
  isCurrent: boolean
  isLoading: boolean
  onSubscribe: () => void
}) {
  return (
    <div
      className={`relative rounded-lg border bg-surface p-4 flex flex-col gap-3 shadow-sm ${
        isCurrent ? 'border-[var(--color-primary)]' : 'border-border'
      }`}
    >
      {isCurrent && (
        <span className="absolute -top-2 left-4 inline-flex items-center px-2 py-0.5 rounded bg-[var(--color-primary)] text-white text-xs font-medium">
          Current
        </span>
      )}
      <div className="flex items-baseline justify-between">
        <h4 className="font-semibold text-fg">{plan.name}</h4>
        <span className="text-xs uppercase text-fg-subtle">{plan.pricingModel}</span>
      </div>
      <Money paise={plan.priceInr} className="text-2xl font-semibold text-fg" />
      {plan.description && <p className="text-sm text-fg-muted">{plan.description}</p>}
      <Button
        variant={isCurrent ? 'ghost' : 'secondary'}
        onClick={onSubscribe}
        loading={isLoading}
        disabled={isCurrent}
        size="sm"
        className="mt-auto"
      >
        {isCurrent ? 'Active plan' : 'Switch to this plan'}
      </Button>
    </div>
  )
}
